import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import TambaLogo from "@/components/TambaLogo";
import { 
  ArrowLeft, 
  Send, 
  Loader2,
  User,
  Bot
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Format AI message with better styling
const FormattedMessage = ({ content, isUser }) => {
  if (isUser) {
    return <p className="text-sm">{content}</p>;
  }
  
  // Helper to render text with bold formatting
  const renderWithBold = (text) => {
    // Handle **bold** markdown
    const parts = text.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((part, i) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={i} className="font-semibold">{part.slice(2, -2)}</strong>;
      }
      return part;
    });
  };
  
  // Process the content to add formatting
  const formatContent = (text) => {
    // Split by double newlines for paragraphs
    const paragraphs = text.split(/\n\n+/);
    
    return paragraphs.map((paragraph, pIndex) => {
      // Check if it's a bullet point list
      const lines = paragraph.split('\n');
      const isBulletList = lines.some(line => 
        line.trim().startsWith('•') || 
        line.trim().startsWith('-') || 
        line.trim().startsWith('*') && !line.trim().startsWith('**')
      );
      
      if (isBulletList) {
        return (
          <ul key={pIndex} className="space-y-2 my-3">
            {lines.map((line, lIndex) => {
              const trimmedLine = line.trim();
              if ((trimmedLine.startsWith('•') || trimmedLine.startsWith('-') || (trimmedLine.startsWith('*') && !trimmedLine.startsWith('**')))) {
                return (
                  <li key={lIndex} className="flex items-start gap-2 text-sm">
                    <span className="text-tamba-teal mt-0.5 flex-shrink-0">•</span>
                    <span>{renderWithBold(trimmedLine.replace(/^[•\-\*]\s*/, ''))}</span>
                  </li>
                );
              }
              return line ? <p key={lIndex} className="text-sm">{renderWithBold(line)}</p> : null;
            })}
          </ul>
        );
      }
      
      // Check for numbered lists
      const isNumberedList = lines.some(line => /^\d+[\.\)]\s/.test(line.trim()));
      
      if (isNumberedList) {
        return (
          <ol key={pIndex} className="space-y-2 my-3">
            {lines.map((line, lIndex) => {
              const trimmedLine = line.trim();
              const match = trimmedLine.match(/^(\d+)[\.\)]\s*(.*)/);
              if (match) {
                return (
                  <li key={lIndex} className="flex items-start gap-2 text-sm">
                    <span className="text-tamba-blue font-semibold min-w-[20px] flex-shrink-0">{match[1]}.</span>
                    <span>{renderWithBold(match[2])}</span>
                  </li>
                );
              }
              return line ? <p key={lIndex} className="text-sm">{renderWithBold(line)}</p> : null;
            })}
          </ol>
        );
      }
      
      // Regular paragraph
      return (
        <p key={pIndex} className="text-sm mb-3 last:mb-0 leading-relaxed">
          {renderWithBold(paragraph)}
        </p>
      );
    });
  };
  
  return <div className="space-y-1">{formatContent(content)}</div>;
};

const ChatPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const storedUser = localStorage.getItem("tamba_user");
    if (!storedUser) {
      navigate("/auth");
      return;
    }
    const userData = JSON.parse(storedUser);
    setUser(userData);
    fetchChatHistory(userData.id);
  }, [navigate]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const fetchChatHistory = async (userId) => {
    try {
      const response = await axios.get(`${API}/chat/history/${userId}`);
      setMessages(response.data || []);
    } catch (error) {
      console.error("Error fetching chat history:", error);
    }
  };

  const handleSend = async () => {
    if (!input.trim() || loading) return;

    const userMessage = {
      id: Date.now().toString(),
      role: "user",
      content: input.trim(),
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput("");
    setLoading(true);

    try {
      const response = await axios.post(`${API}/chat`, {
        message: userMessage.content,
        user_id: user.id
      });

      const assistantMessage = {
        id: (Date.now() + 1).toString(),
        role: "assistant",
        content: response.data.response,
        timestamp: new Date().toISOString()
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      toast.error("Failed to get response. Please try again.");
      console.error("Chat error:", error);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const suggestedQuestions = [
    "How can I save more money each month?",
    "What's a good budget breakdown for my income?",
    "Tips for building an emergency fund",
    "How should I prioritize my financial goals?"
  ];

  return (
    <div className="h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="flex-shrink-0 bg-background/80 backdrop-blur-xl border-b border-border/50 px-4 py-3">
        <div className="max-w-3xl mx-auto flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate("/dashboard")}
            data-testid="back-btn"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex items-center gap-3">
            <TambaLogo className="h-10 w-auto" />
            <div>
              <h1 className="font-heading text-lg font-semibold">AI Advisor</h1>
              <p className="text-xs text-muted-foreground">Your financial coach</p>
            </div>
          </div>
        </div>
      </header>

      {/* Chat Area */}
      <ScrollArea className="flex-1 p-4" ref={scrollRef}>
        <div className="max-w-3xl mx-auto space-y-4 pb-4">
          {/* Welcome message if no history */}
          {messages.length === 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center py-12"
            >
              <TambaLogo className="h-20 w-20 mx-auto mb-4" />
              <h2 className="font-heading text-xl font-semibold mb-2">Hello, {user?.name || "there"}!</h2>
              <p className="text-muted-foreground mb-8 max-w-md mx-auto">
                I'm your AI financial advisor. Ask me anything about budgeting, saving, investing, or building wealth.
              </p>
              
              <div className="grid sm:grid-cols-2 gap-4 max-w-2xl mx-auto px-4">
                {suggestedQuestions.map((question, index) => {
                  const colors = [
                    "border-tamba-blue/30 hover:border-tamba-blue/50 hover:bg-tamba-blue/5 bg-card",
                    "border-tamba-yellow/30 hover:border-tamba-yellow/50 hover:bg-tamba-yellow/5 bg-card",
                    "border-tamba-coral/30 hover:border-tamba-coral/50 hover:bg-tamba-coral/5 bg-card",
                    "border-tamba-teal/30 hover:border-tamba-teal/50 hover:bg-tamba-teal/5 bg-card"
                  ];
                  return (
                    <Button
                      key={index}
                      variant="outline"
                      className={`h-auto min-h-[60px] py-4 px-5 text-left text-sm rounded-xl whitespace-normal leading-relaxed ${colors[index]}`}
                      onClick={() => {
                        setInput(question);
                        inputRef.current?.focus();
                      }}
                      data-testid={`suggested-question-${index}`}
                    >
                      {question}
                    </Button>
                  );
                })}
              </div>
            </motion.div>
          )}

          {/* Messages */}
          <AnimatePresence mode="popLayout">
            {messages.map((message, index) => (
              <motion.div
                key={message.id || index}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className={`flex gap-3 ${message.role === "user" ? "justify-end" : "justify-start"}`}
              >
                {message.role === "assistant" && (
                  <div className="w-8 h-8 rounded-full bg-tamba-blue/10 flex items-center justify-center flex-shrink-0 mt-1">
                    <Bot className="h-4 w-4 text-tamba-blue" />
                  </div>
                )}
                
                <Card className={`max-w-[80%] p-4 ${
                  message.role === "user" 
                    ? "bg-tamba-blue text-white rounded-br-sm" 
                    : "bg-card border-border/50 rounded-bl-sm"
                }`}>
                  <FormattedMessage content={message.content} isUser={message.role === "user"} />
                </Card>
                
                {message.role === "user" && (
                  <div className="w-8 h-8 rounded-full bg-tamba-teal/10 flex items-center justify-center flex-shrink-0 mt-1">
                    <User className="h-4 w-4 text-tamba-teal" />
                  </div>
                )}
              </motion.div>
            ))}
          </AnimatePresence>

          {/* Loading indicator */}
          {loading && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex gap-3"
            >
              <div className="w-8 h-8 rounded-full bg-tamba-blue/10 flex items-center justify-center">
                <Bot className="h-4 w-4 text-tamba-blue" />
              </div>
              <Card className="p-4 bg-card border-border/50">
                <div className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin text-tamba-blue" />
                  <span className="text-sm text-muted-foreground">Thinking...</span>
                </div>
              </Card>
            </motion.div>
          )}
        </div>
      </ScrollArea>

      {/* Input Area */}
      <div className="flex-shrink-0 border-t border-border/50 bg-background/80 backdrop-blur-xl p-4">
        <div className="max-w-3xl mx-auto">
          <div className="flex gap-3">
            <Input
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Ask me anything about your finances..."
              className="flex-1 h-12 rounded-xl bg-secondary/50 border-0 focus-visible:ring-tamba-blue"
              disabled={loading}
              data-testid="chat-input"
            />
            <Button
              onClick={handleSend}
              disabled={!input.trim() || loading}
              className="h-12 w-12 rounded-xl bg-tamba-blue hover:bg-tamba-blue/90"
              data-testid="send-btn"
            >
              {loading ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <Send className="h-5 w-5" />
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatPage;
